// Audio (16.69) -- the first real sound layer in the game.
//
// **Procedurally synthesized, not sampled.** Every effect is built live
// from Web Audio oscillators/noise + gain envelopes rather than shipped
// as .wav/.mp3 assets. Three reasons this is the right call here and not
// a shortcut: the repo carries no binary assets today (nothing to
// source, license, or bloat the bundle with), the deploy target is a
// plain static IIS folder (no asset pipeline), and each sound is derived
// from the same weapon/event identity the visual FX already use (16.59),
// so audio and visuals stay in sync by construction instead of by
// convention.
//
// Every entry point is a no-op when Web Audio is unavailable (SSR, the
// vitest/node test environment, a browser with it disabled) -- importing
// this module must never throw, and no caller should have to guard.
//
// Autoplay policy: browsers refuse to start an AudioContext outside a
// user gesture, so the context is created lazily on first playback and
// `resumeAudio()` is called from the app's own first real interaction.

import { effectiveSfxGain, settings } from "./settings";

export type SoundName =
  | "laser"
  | "auto"
  | "missile"
  | "explosion"
  | "mining"
  | "shieldRepair"
  | "hullRepair"
  | "hostileFire"
  | "alert";

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let unavailable = false;

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function ensureContext(): AudioContext | null {
  if (unavailable) return null;
  if (ctx) return ctx;
  const Ctor = audioContextCtor();
  if (!Ctor) {
    unavailable = true;
    return null;
  }
  try {
    ctx = new Ctor();
    masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(ctx.destination);
    return ctx;
  } catch {
    unavailable = true;
    return null;
  }
}

// Called from the app's first real user gesture -- an AudioContext
// created before one exists starts "suspended" and stays silent until
// explicitly resumed.
export function resumeAudio(): void {
  const c = ensureContext();
  if (c && c.state === "suspended") {
    void c.resume().catch(() => {
      /* best-effort: a refused resume just means silence, never a crash */
    });
  }
}

export function audioAvailable(): boolean {
  return !unavailable && audioContextCtor() !== null;
}

// One shared noise buffer, generated once -- explosions/thruster-style
// effects all filter the same white noise rather than each allocating
// their own multi-second buffer.
let noiseBuffer: AudioBuffer | null = null;
function getNoiseBuffer(c: AudioContext): AudioBuffer {
  if (!noiseBuffer) {
    const length = Math.floor(c.sampleRate * 1.0);
    noiseBuffer = c.createBuffer(1, length, c.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
  }
  return noiseBuffer;
}

interface ToneSpec {
  type: OscillatorType;
  startHz: number;
  endHz: number;
  durationS: number;
  gain: number;
  delayS?: number;
}

function playTone(c: AudioContext, dest: GainNode, spec: ToneSpec): void {
  const start = c.currentTime + (spec.delayS ?? 0);
  const osc = c.createOscillator();
  const env = c.createGain();
  osc.type = spec.type;
  osc.frequency.setValueAtTime(spec.startHz, start);
  if (spec.endHz !== spec.startHz) {
    // exponentialRamp can't cross or reach zero -- both endpoints are
    // real positive frequencies here, so this is safe.
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, spec.endHz), start + spec.durationS);
  }
  // A tiny attack instead of an instant jump to full gain: a hard start
  // on a raw oscillator produces an audible click on every shot.
  env.gain.setValueAtTime(0, start);
  env.gain.linearRampToValueAtTime(spec.gain, start + 0.005);
  env.gain.exponentialRampToValueAtTime(0.0001, start + spec.durationS);
  osc.connect(env);
  env.connect(dest);
  osc.start(start);
  osc.stop(start + spec.durationS + 0.02);
}

interface NoiseSpec {
  durationS: number;
  gain: number;
  filterStartHz: number;
  filterEndHz: number;
  delayS?: number;
}

function playNoise(c: AudioContext, dest: GainNode, spec: NoiseSpec): void {
  const start = c.currentTime + (spec.delayS ?? 0);
  const src = c.createBufferSource();
  src.buffer = getNoiseBuffer(c);
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(spec.filterStartHz, start);
  filter.frequency.exponentialRampToValueAtTime(
    Math.max(20, spec.filterEndHz),
    start + spec.durationS
  );
  const env = c.createGain();
  env.gain.setValueAtTime(0, start);
  env.gain.linearRampToValueAtTime(spec.gain, start + 0.005);
  env.gain.exponentialRampToValueAtTime(0.0001, start + spec.durationS);
  src.connect(filter);
  filter.connect(env);
  env.connect(dest);
  src.start(start);
  src.stop(start + spec.durationS + 0.02);
}

// Each sound's character deliberately mirrors its 16.59 visual: the Laser
// is one bright instant zap, the Auto-Turret is three staggered clicks at
// the same 70ms spacing its tracer beams use, the Missile is a launch
// whoosh that lands as a separate explosion, and hostile fire is the same
// shape as the player's own laser but pitched down, so incoming reads as
// distinct without being a whole new sound vocabulary.
function renderSound(c: AudioContext, dest: GainNode, name: SoundName): void {
  switch (name) {
    case "laser":
      playTone(c, dest, { type: "square", startHz: 1400, endHz: 320, durationS: 0.18, gain: 0.18 });
      break;
    case "auto":
      for (let i = 0; i < 3; i++) {
        playNoise(c, dest, {
          durationS: 0.06,
          gain: 0.16,
          filterStartHz: 3800,
          filterEndHz: 700,
          delayS: i * 0.07 // matches spawnBeam's own 70ms tracer stagger
        });
      }
      break;
    case "missile":
      playNoise(c, dest, { durationS: 0.45, gain: 0.12, filterStartHz: 400, filterEndHz: 2600 });
      playTone(c, dest, { type: "sawtooth", startHz: 180, endHz: 90, durationS: 0.4, gain: 0.08 });
      break;
    case "explosion":
      playNoise(c, dest, { durationS: 0.7, gain: 0.3, filterStartHz: 2200, filterEndHz: 60 });
      playTone(c, dest, { type: "sine", startHz: 120, endHz: 40, durationS: 0.5, gain: 0.18 });
      break;
    case "mining":
      // Short pulse per extraction tick, not a held drone -- the Mining
      // Laser's visual beam is continuous, but retriggering a sustained
      // loop from the frame loop would stack voices without bound.
      playTone(c, dest, { type: "triangle", startHz: 220, endHz: 260, durationS: 0.22, gain: 0.09 });
      break;
    case "shieldRepair":
      playTone(c, dest, { type: "sine", startHz: 520, endHz: 880, durationS: 0.3, gain: 0.12 });
      break;
    case "hullRepair":
      playTone(c, dest, { type: "sine", startHz: 300, endHz: 480, durationS: 0.3, gain: 0.12 });
      break;
    case "hostileFire":
      playTone(c, dest, { type: "sawtooth", startHz: 700, endHz: 160, durationS: 0.2, gain: 0.14 });
      break;
    case "alert":
      // Two-tone rising warning, deliberately the only non-combat sound --
      // used for hazard telegraphs and mutiny warnings, where the point is
      // to interrupt whatever the captain was focused on.
      playTone(c, dest, { type: "square", startHz: 440, endHz: 440, durationS: 0.16, gain: 0.14 });
      playTone(c, dest, {
        type: "square",
        startHz: 660,
        endHz: 660,
        durationS: 0.22,
        gain: 0.14,
        delayS: 0.18
      });
      break;
  }
}

// Fire-and-forget. Safe to call from the frame loop: silent (and
// allocation-free past the early return) whenever audio is unavailable,
// muted, or turned all the way down.
export function playSound(name: SoundName): void {
  const gain = effectiveSfxGain(settings);
  if (gain <= 0) return;
  const c = ensureContext();
  if (!c || !masterGain) return;
  // A context suspended by autoplay policy would queue every scheduled
  // node and dump them all at once on resume -- drop instead.
  if (c.state !== "running") {
    resumeAudio();
    return;
  }
  try {
    const bus = c.createGain();
    bus.gain.value = gain;
    bus.connect(masterGain);
    renderSound(c, bus, name);
  } catch {
    /* a failed voice must never break the frame loop */
  }
}

// Plays a short confirmation of the CURRENT volume settings -- used by the
// Options screen's sliders so the captain can hear what they're setting.
export function previewVolume(): void {
  playSound("shieldRepair");
}
